import type { ThemeConfig } from "@/types/theme";

export const tailwindNeutral: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-neutral-700 dark:text-neutral-400",
    secondary: "text-neutral-900 dark:text-neutral-500",
    accent: "text-blue-600 dark:text-blue-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-neutral-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-neutral-600 dark:bg-neutral-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-neutral-50 to-gray-100 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-neutral-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-neutral-600 dark:bg-neutral-500",
    backgroundSecondaryColor: "bg-neutral-800 dark:bg-neutral-700",
    backgroundAccentColor: "bg-blue-500 dark:bg-blue-400",
    backgroundPrimarySubtle: "bg-neutral-50 dark:bg-neutral-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-neutral-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-neutral-700 dark:text-neutral-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-neutral-600 dark:text-neutral-400",
    iconBackgroundFilled: "bg-neutral-100 dark:bg-neutral-950",
    iconBackgroundGradient: "bg-gradient-to-br from-neutral-500 to-blue-500",
    iconBackgroundDefault: "bg-neutral-50 dark:bg-neutral-950/50",
    iconBorder: "border-neutral-200 dark:border-neutral-800",

    // Border classes
    borderDefault: "border-neutral-200 dark:border-gray-700",
    borderLight: "border-neutral-100 dark:border-gray-800",
    borderStrong: "border-neutral-300 dark:border-gray-600",
    borderPrimary: "border-neutral-700 dark:border-neutral-500",
    borderSecondary: "border-neutral-900 dark:border-neutral-700",
    borderBottom: "border-b border-neutral-200 dark:border-gray-700",
    borderAccent: "border-blue-600 dark:border-blue-400",

    // Badge classes
    badgeBackground: "bg-neutral-100 dark:bg-neutral-950",
    badgeText: "text-neutral-700 dark:text-neutral-300",

    // Shadow classes
    shadowPrimary: "shadow-neutral-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-neutral-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(115,115,115,0.30)]", // neutral-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-neutral-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-neutral-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-neutral-600 hover:bg-neutral-700 dark:bg-neutral-700 dark:hover:bg-neutral-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-neutral-300 dark:bg-neutral-600",
    textEmphasis: "text-indigo-600 dark:text-indigo-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-neutral-600 to-neutral-700",
    gradientSecondary: "bg-gradient-to-br from-neutral-700 to-neutral-900",
    gradientAccent: "bg-gradient-to-r from-neutral-500 to-blue-500",
    gradientSubtle:
      "bg-gradient-to-br from-neutral-50 to-gray-100 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-neutral-600 via-blue-500 to-neutral-600",
    gradientHero:
      "bg-gradient-to-br from-neutral-600 via-blue-500 to-neutral-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
